'use client';

import { Link } from '@/ui/Link';
import { Menu, Transition } from '@headlessui/react';
import { useLocale } from '@vimond/i18n/hooks/useLocale';
import { getEnvVar } from '@vimond/utils/env';
import { useTheme } from 'next-themes';
import { Fragment } from 'react';

type LanguageSwitchProps = {
  color?: string;
  menuposition?: 'over' | 'under';
};

export const LanguageSwitcher = ({
  color,
  menuposition,
}: LanguageSwitchProps) => {
  const { resolvedTheme } = useTheme();
  const { locale: currentLocale, locales, pathnameWithoutLocale } = useLocale();

  if (getEnvVar('NEXT_PUBLIC_LANGUAGE_SWITCHER_ENABLED') === 'false')
    return null;

  const selectedLanguage =
    locales?.find((l) => l.locale === currentLocale)?.language ?? 'Language';
  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button
          as="button"
          className="inline-flex items-center justify-center border border-gray-400 rounded px-3 py-1 text-sm font-medium text-button-secondary-text hover:ring-2 hover:ring-offset-1 hover:ring-indigo-500 focus:outline-none"
        >
          <span
            className={`${color || resolvedTheme === 'dark' || menuposition !== 'over' ? 'text-white' : 'text-inherit'}`}
          >
            {selectedLanguage}
          </span>
          <svg
            className="ml-2 -mr-1 h-5 w-5 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill={color ?? 'currentColor'}
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
              clipRule="evenodd"
            />
          </svg>
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={`absolute right-0 mt-2 w-40 origin-top-right rounded-md bg-background-primary shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 ${
            menuposition === 'over' ? '-translate-y-full translate-x-full' : ''
          } max-h-[11.25rem] overflow-y-auto`}
        >
          {locales?.map((locale) => (
            <Menu.Item as={Fragment} key={locale.localeId}>
              {({ active }) => (
                <Link
                  href={`/${locale.locale}${pathnameWithoutLocale}`}
                  className={`block h-8 px-4 py-2 text-sm ${
                    active
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-button-secondary-text'
                  } ${locale.locale === currentLocale ? 'font-bold' : ''}`}
                >
                  {locale.language}
                </Link>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};
